import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  onDemoRequest: () => void;
}

const HeroSection = ({ onDemoRequest }: HeroSectionProps) => {
  return (
    <section className="relative bg-gradient-to-br from-primary via-primary/95 to-secondary py-20 lg:py-32 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div className="inline-flex items-center space-x-2 bg-accent/20 text-accent-foreground px-4 py-2 rounded-full">
              <Icon name="SparklesIcon" size={20} variant="solid" />
              <span className="text-sm font-heading font-semibold">AI-Powered Support Excellence</span>
            </div>

            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-heading font-bold text-primary-foreground leading-tight">
              Transform Customer Support with Intelligent AI Automation
            </h1>

            <p className="text-lg lg:text-xl text-white leading-relaxed">
              Reduce response times by 85%, cut support costs by 60%, and deliver 24/7 exceptional customer experiences with our enterprise-grade AI support systems.
            </p>

            {/* Key Metrics */}
            <div className="grid grid-cols-3 gap-6 pt-4">
              <div className="text-center lg:text-left">
                <div className="text-3xl lg:text-4xl font-heading font-bold text-accent">85%</div>
                <div className="text-sm text-white mt-1">Faster Response</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-3xl lg:text-4xl font-heading font-bold text-success">60%</div>
                <div className="text-sm text-white mt-1">Cost Reduction</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-3xl lg:text-4xl font-heading font-bold text-accent">24/7</div>
                <div className="text-sm text-white mt-1">Availability</div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button
                onClick={onDemoRequest}
                className="px-8 py-4 bg-accent text-accent-foreground font-heading font-semibold rounded-lg hover:bg-accent/90 hover:shadow-elevated transition-all duration-300 transform hover:-translate-y-0.5 flex items-center justify-center space-x-2"
              >
                <span>Request Live Demo</span>
                <Icon name="ArrowRightIcon" size={20} />
              </button>
              <button className="px-8 py-4 bg-primary-foreground/10 text-primary-foreground font-heading font-semibold rounded-lg hover:bg-primary-foreground/20 transition-all duration-300 backdrop-blur-sm flex items-center justify-center space-x-2">
                <Icon name="CalculatorIcon" size={20} />
                <span>Calculate ROI</span>
              </button>
            </div>
          </div>

          {/* Visual Element */}
          <div className="relative hidden lg:block">
            <div className="relative bg-primary-foreground/10 backdrop-blur-sm rounded-2xl p-8 border border-primary-foreground/20">
              <div className="space-y-4">
                {/* Chat Interface Preview */}
                <div className="bg-primary-foreground rounded-lg p-4 shadow-lg">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center">
                      <Icon name="ChatBubbleLeftRightIcon" size={20} className="text-accent-foreground" />
                    </div>
                    <div>
                      <div className="text-sm font-heading font-semibold text-foreground">AI Support Assistant</div>
                      <div className="text-xs text-muted-foreground">Online • Avg response: 2s</div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-muted rounded-lg p-3">
                      <p className="text-sm text-foreground">How can I track my order?</p>
                    </div>
                    <div className="bg-accent/10 rounded-lg p-3">
                      <p className="text-sm text-foreground">I can help you track your order! Please provide your order number, and I'll get you the latest status immediately.</p>
                    </div>
                  </div>
                </div>

                {/* Metrics Cards */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-primary-foreground rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="ClockIcon" size={16} className="text-success" />
                      <span className="text-xs font-heading font-semibold text-primary">Response Time</span>
                    </div>
                    <div className="text-2xl font-heading font-bold text-primary">2.3s</div>
                  </div>
                  <div className="bg-primary-foreground rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="FaceSmileIcon" size={16} className="text-accent" />
                      <span className="text-xs font-heading font-semibold text-primary">Satisfaction</span>
                    </div>
                    <div className="text-2xl font-heading font-bold text-primary">98%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;